'use client';
import { Flex, Text } from '@skin-seoul/design-system';
import type { ComponentProps } from 'react';
import { CountryCurrency, useCartService } from '@skin-seoul/service';
import { LegacyCartIcon } from '../../assets/LegacyCartIcon';
import QueryBoundary from '../../utils/react/QueryBoundary';
import type { Country } from '@skin-seoul/service';
import { ClientOnly } from '@skin-seoul/utils';

const SHOPPING_BAG_BUTTON_TEST_ID = 'shopping-bag-button';

const ProductQuantity = ({ country }: { country: Country }) => {
  const { useGetCartSuspenseQuery } = useCartService();
  const { data } = useGetCartSuspenseQuery({ currency: CountryCurrency[country] });

  const totalQuantity = data?.cart?.item?.reduce((prev, curr) => prev + curr.quantity, 0) || 0;

  return (
    <Text textStyle="label.small" color="generic.0">
      {totalQuantity}
    </Text>
  );
};

type ShoppingBagButtonProps = ComponentProps<typeof Flex> & {
  country: Country;
  iconProps?: ComponentProps<typeof LegacyCartIcon>;
};

const ShoppingBagButton = ({ country, iconProps = { color: 'generic.0' }, ...rest }: ShoppingBagButtonProps) => {
  return (
    <Flex
      cursor="pointer"
      data-testid={SHOPPING_BAG_BUTTON_TEST_ID}
      w="fit-content"
      h="fit-content"
      position="relative"
      {...rest}
    >
      <LegacyCartIcon cursor="pointer" height="24px" width="24px" {...iconProps} />
      <ClientOnly>
        <Flex
          alignItems="center"
          backgroundColor="#027A48"
          borderRadius="14px"
          textStyle="label.small"
          height="16px"
          justifyContent="center"
          position="absolute"
          right="-4px"
          top="-4px"
          width="16px"
        >
          <QueryBoundary
            loadingFallback={
              <Text textStyle="label.small" color="generic.0">
                0
              </Text>
            }
            errorFallback={
              <Text textStyle="label.small" color="generic.0">
                0
              </Text>
          }
          >
            <ProductQuantity country={country} />
          </QueryBoundary>
        </Flex>
      </ClientOnly>
    </Flex>
  );
};

export default ShoppingBagButton;
